import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { apiError } from "../../api";
import { Spinner } from "../../components/Logo";
import { PageHeader, StatusChip } from "../../components/ui";
import { Modal, Field, ConfirmButton } from "../../components/Modal";
import { Plus, Pencil, Trash2, Megaphone } from "lucide-react";

const empty = { title: "", body: "", audience: "all", status: "published", image_url: "" };

export default function AdminAnnouncements() {
  const [rows, setRows] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);

  const load = () => api.get("/admin/announcements").then((r) => setRows(r.data.announcements)).catch(() => {});
  useEffect(() => { load(); }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const openNew = () => { setForm(empty); setEditId(null); setOpen(true); };
  const openEdit = (a) => { setForm({ ...a }); setEditId(a.id); setOpen(true); };
  const save = async () => {
    try {
      if (editId) await api.patch(`/admin/announcements/${editId}`, form);
      else await api.post("/admin/announcements", form);
      toast.success(editId ? "Updated" : "Published"); setOpen(false); load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const del = async (id) => { try { await api.delete(`/admin/announcements/${id}`); toast.success("Deleted"); load(); } catch (e) { toast.error(apiError(e)); } };

  return (
    <div data-testid="admin-announcements">
      <PageHeader overline="Communication" title="Announcements" desc="Publish news and updates that appear on every learner's dashboard."
        actions={<button className="btn-primary" onClick={openNew} data-testid="new-ann-btn"><Plus size={18} /> New announcement</button>} />

      {!rows ? <Spinner /> : (
        <div className="space-y-3">
          {rows.map((a) => (
            <div key={a.id} className="card p-5 flex items-start gap-4" data-testid={`admin-ann-${a.id}`}>
              <div className="h-10 w-10 rounded-lg bg-[#F26522]/15 flex items-center justify-center shrink-0"><Megaphone size={18} className="text-[#F26522]" /></div>
              <div className="flex-1">
                <div className="flex items-center gap-2"><h3 className="font-semibold">{a.title}</h3><StatusChip status={a.status} /></div>
                <p className="text-slate-400 text-sm mt-1">{a.body}</p>
                <p className="text-xs text-slate-500 mt-2">{new Date(a.created_at).toLocaleString()}</p>
              </div>
              <div className="flex items-center gap-1">
                <button className="btn-ghost !px-2" onClick={() => openEdit(a)}><Pencil size={16} /></button>
                <ConfirmButton className="btn-ghost !px-2 text-red-400" onConfirm={() => del(a.id)}><Trash2 size={16} /></ConfirmButton>
              </div>
            </div>
          ))}
          {rows.length === 0 && <div className="card p-10 text-center text-slate-500">No announcements yet.</div>}
        </div>
      )}

      <Modal open={open} onClose={() => setOpen(false)} title={editId ? "Edit announcement" : "New announcement"}>
        <div className="space-y-4">
          <Field label="Title"><input className="input" value={form.title} onChange={set("title")} data-testid="ann-title" /></Field>
          <Field label="Body"><textarea className="input min-h-28" value={form.body || ""} onChange={set("body")} data-testid="ann-body" /></Field>
          <Field label="Status"><select className="input" value={form.status} onChange={set("status")}>{["published", "draft"].map((s) => <option key={s}>{s}</option>)}</select></Field>
          <button className="btn-primary w-full" onClick={save} data-testid="save-ann-btn">{editId ? "Update" : "Publish"}</button>
        </div>
      </Modal>
    </div>
  );
}
